import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import {
  FiActivity,
  FiBarChart2,
  FiBriefcase,
  FiChevronLeft,
  FiChevronRight,
  FiCreditCard,
  FiFileText,
  FiFolder,
  FiHome,
  FiLogOut,
  FiLoader,
  FiMessageSquare,
  FiSettings,
  FiX,
  FiZap,
} from "react-icons/fi";

import SafeIcon from "../../common/SafeIcon";
import { useClient } from "../../context/ClientContext";

/* =========================================================
   PROFITYO CLIENT PORTAL NAVIGATION
========================================================= */

const navigation = [
  {
    label: "WORKSPACE",
    items: [
      {
        name: "Dashboard",
        icon: FiHome,
        path: "/client",
        available: true,
      },
      {
        name: "AI Copilot",
        icon: FiZap,
        path: "/client/ai",
        available: true,
        featured: true,
      },
    ],
  },
  {
    label: "FINANCIAL MANAGEMENT",
    items: [
      {
        name: "Transactions",
        icon: FiActivity,
        path: "/client/transactions",
        available: true,
      },
      {
        name: "Invoices",
        icon: FiFileText,
        path: "/client/invoices",
        available: true,
      },
      {
        name: "Bills & Expenses",
        icon: FiCreditCard,
        path: "/client/bills",
        available: true,
      },
      {
        name: "Banking",
        icon: FiBriefcase,
        path: "/client/banking",
        available: true,
      },
      {
        name: "Reports",
        icon: FiBarChart2,
        path: "/client/reports",
        available: true,
      },
    ],
  },
  {
    label: "COLLABORATION",
    items: [
      {
        name: "Documents",
        icon: FiFolder,
        path: "/client/documents",
        available: true,
      },
      {
        name: "My Accountant",
        icon: FiMessageSquare,
        path: "/client/my-accountant",
        available: true,
      },
    ],
  },
  {
    label: "PREFERENCES",
    items: [
      {
        name: "Settings",
        icon: FiSettings,
        path: "/client/settings",
        available: true,
      },
    ],
  },
];

/* =========================================================
   NAVIGATION ITEM
========================================================= */

function NavigationItem({
  item,
  collapsed,
  onNavigate,
}) {
  const baseClasses =
    "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors";

  if (!item.available) {
    return (
      <div
        aria-disabled="true"
        title={`${item.name} — Coming soon`}
        className={`${baseClasses} cursor-not-allowed text-slate-400`}
      >
        <SafeIcon
          icon={item.icon}
          className={`shrink-0 text-lg ${
            item.featured ? "text-violet-400" : ""
          }`}
        />

        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left">
              {item.name}
            </span>

            <span className="text-[10px] font-medium text-slate-400">
              Soon
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.path === "/client"}
      title={collapsed ? item.name : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        `${baseClasses} ${
          isActive
            ? item.featured
              ? "bg-violet-50 font-bold text-violet-700"
              : "bg-emerald-50 font-bold text-emerald-700"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      <SafeIcon
        icon={item.icon}
        className={`shrink-0 text-lg ${
          item.featured ? "text-violet-600" : ""
        }`}
      />

      <span
        className={
          collapsed
            ? "min-w-0 flex-1 truncate text-left md:hidden"
            : "min-w-0 flex-1 truncate text-left"
        }
      >
        {item.name}
      </span>

      {item.featured && (
        <span
          className={`rounded-md bg-violet-100 px-2 py-1 text-[10px] font-bold text-violet-700 ${
            collapsed ? "md:hidden" : ""
          }`}
        >
          AI
        </span>
      )}
    </NavLink>
  );
}

/* =========================================================
   CLIENT SIDEBAR
========================================================= */

export default function ClientSidebar({
  isOpen = false,
  onClose = () => {},
  isCollapsed = false,
  setIsCollapsed = () => {},
}) {
  const navigate = useNavigate();

  const {
    activeWorkspace,
    profile,
    logout,
  } = useClient();

  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");

  const organizationName =
    activeWorkspace?.name || "My Business";

  const fullName = [
    profile?.first_name,
    profile?.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const initial = (
    fullName ||
    organizationName ||
    "P"
  )
    .charAt(0)
    .toUpperCase();

  const sidebarWidth = isCollapsed
    ? "md:w-20"
    : "md:w-72";

  /* =========================================================
     SIGN OUT

     Uses the existing ClientContext logout method.
  ========================================================= */

  const handleSignOut = async () => {
    if (signingOut) return;

    setSigningOut(true);
    setSignOutError("");

    try {
      if (typeof logout !== "function") {
        throw new Error(
          "Client sign-out is currently unavailable."
        );
      }

      const result = await logout();

      if (result?.success === false) {
        throw new Error(
          result?.error || "Unable to sign out."
        );
      }

      onClose();

      navigate("/login", {
        replace: true,
      });
    } catch (error) {
      console.error("Client sign-out failed:", error);

      setSignOutError(
        error?.message ||
          "Unable to sign out. Please try again."
      );
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <>
      {/* MOBILE BACKDROP */}

      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation overlay"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
        />
      )}

      {/* SIDEBAR */}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          w-72 transform border-r border-slate-200
          bg-white transition-all duration-300
          md:translate-x-0
          ${sidebarWidth}
          ${
            isOpen
              ? "translate-x-0"
              : "-translate-x-full"
          }
        `}
      >
        <div className="flex h-full min-h-0 flex-col bg-white">

          {/* BRAND */}

          <div
            className={`flex h-[76px] shrink-0 items-center border-b border-slate-100 px-5 ${
              isCollapsed
                ? "md:justify-center md:px-2"
                : "justify-between"
            }`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                <SafeIcon
                  icon={FiBriefcase}
                  className="text-xl"
                />
              </div>

              <div
                className={
                  isCollapsed
                    ? "min-w-0 md:hidden"
                    : "min-w-0"
                }
              >
                <h2 className="text-lg font-black tracking-tight text-slate-900">
                  ProfitYo
                </h2>

                <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                  Client Portal
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close client navigation"
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 md:hidden"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>

          {/* ACTIVE BUSINESS */}

          <div
            className={
              isCollapsed
                ? "border-b border-slate-100 p-3 md:px-2"
                : "border-b border-slate-100 p-4"
            }
          >
            <div
              className={`flex items-center gap-3 rounded-2xl bg-slate-50 p-3 ${
                isCollapsed
                  ? "md:justify-center md:px-2"
                  : ""
              }`}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm">
                <SafeIcon icon={FiBriefcase} />
              </div>

              <div
                className={
                  isCollapsed
                    ? "min-w-0 md:hidden"
                    : "min-w-0"
                }
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Active Business
                </p>

                <p
                  title={organizationName}
                  className="mt-1 truncate text-xs font-bold text-slate-800"
                >
                  {organizationName}
                </p>
              </div>
            </div>
          </div>

          {/* NAVIGATION */}

          <nav
            aria-label="Client workspace navigation"
            className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-5"
          >
            {navigation.map((section) => (
              <div key={section.label}>
                <p
                  className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${
                    isCollapsed ? "md:hidden" : ""
                  }`}
                >
                  {section.label}
                </p>

                <div className="space-y-1">
                  {section.items.map((item) => (
                    <NavigationItem
                      key={item.path}
                      item={item}
                      collapsed={isCollapsed}
                      onNavigate={onClose}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* PROFILE FOOTER */}

          <div className="shrink-0 border-t border-slate-100 bg-white p-3">

            {/* USER PROFILE */}

            <div
              className={`flex items-center gap-3 rounded-xl px-2 py-3 ${
                isCollapsed
                  ? "md:justify-center"
                  : ""
              }`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-sm font-bold text-emerald-800">
                {initial}
              </div>

              <div
                className={
                  isCollapsed
                    ? "min-w-0 md:hidden"
                    : "min-w-0"
                }
              >
                <p className="truncate text-xs font-bold text-slate-800">
                  {fullName || "ProfitYo User"}
                </p>

                <p className="mt-1 text-[10px] capitalize text-slate-500">
                  {activeWorkspace?.role
                    ?.replaceAll("_", " ") ||
                    "Client Member"}
                </p>
              </div>
            </div>

            {/* SIGN OUT ERROR */}

            {signOutError && (
              <div
                role="alert"
                className="mb-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700"
              >
                {signOutError}
              </div>
            )}

            {/* SIGN OUT */}

            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              aria-label="Sign Out"
              title={isCollapsed ? "Sign Out" : undefined}
              className={`mt-2 flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 ${
                isCollapsed
                  ? "md:justify-center"
                  : ""
              }`}
            >
              <SafeIcon
                icon={
                  signingOut
                    ? FiLoader
                    : FiLogOut
                }
                className={`shrink-0 text-lg ${
                  signingOut ? "animate-spin" : ""
                }`}
              />

              <span
                className={
                  isCollapsed ? "md:hidden" : ""
                }
              >
                {signingOut
                  ? "Signing Out..."
                  : "Sign Out"}
              </span>
            </button>

            {/* DESKTOP COLLAPSE BUTTON */}

            <button
              type="button"
              onClick={() =>
                setIsCollapsed(!isCollapsed)
              }
              aria-label={
                isCollapsed
                  ? "Expand sidebar"
                  : "Collapse sidebar"
              }
              title={
                isCollapsed
                  ? "Expand sidebar"
                  : undefined
              }
              className="mt-2 hidden w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 md:flex"
            >
              <SafeIcon
                icon={
                  isCollapsed
                    ? FiChevronRight
                    : FiChevronLeft
                }
              />

              {!isCollapsed && (
                <span className="text-xs font-semibold">
                  Collapse Sidebar
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}